import React, { useEffect, useState } from 'react'
import { Button, Input, Modal } from 'antd'

import { useIsMobile } from '../../hook/useMediaQuery'
import FieldErrorText from './FieldErrorText'
import { renderPasswordVisibilityIcon } from './passwordVisibilityIcon'
import {
  LINKED_ACCOUNT_DIALOG_CLASS,
  LINKED_ACCOUNT_DIALOG_PROMPT_CLASS,
  LINKED_ACCOUNT_PROMPT_MODAL_WIDTH_PX,
  LINKED_ACCOUNT_SHORT_MODAL_WIDTH_PX,
} from '../../constants/linkedAccount'

/**
 * Hộp thoại yêu cầu người dùng đang đăng nhập nhập lại MẬT KHẨU ERP CỦA CHÍNH HỌ (SP-005, Task 4).
 * Dùng chung cho hai nơi: `RevealPasswordModal` (trạng thái 1, cùng file cụm) và hộp "Xác nhận thay
 * đổi" khi lưu form Sửa (Task 6, chưa viết — component này chỉ cung cấp phần dùng chung).
 *
 * 🔴 Bảo mật (Global Constraint 8, "ô mật khẩu rỗng mỗi lần mở + xoá ngay sau mỗi lần Xác nhận"):
 * - `destroyOnClose` của antd Modal KHÔNG đủ — nơi gọi (`RevealPasswordModal`) có thể mở lại hộp
 *   này nhiều lần trên CÙNG một instance component (không đổi `key`), nên state cục bộ ở đây phải
 *   TỰ reset theo prop `open`, không dựa vào việc React unmount/remount.
 * - Xoá ô ngay sau MỖI lần bấm Xác nhận (kể cả khi request đang bay hay sau này báo lỗi) — không
 *   giữ lại giá trị vừa gõ để gửi lại lần hai bằng tay.
 *
 * DOM hook cho Task 6 và bộ test trình duyệt:
 * - `.password-prompt-dialog` — className của `Modal` (render qua PORTAL vào `document.body`).
 * - `.password-prompt-dialog__input` — bọc ngoài `Input.Password`; input thật nằm ở
 *   `.password-prompt-dialog__input input`.
 * - `.password-prompt-dialog__error` — câu lỗi dưới ô nhập, chỉ render khi `errorMessage !== null`.
 * - `.password-prompt-dialog__cancel-btn` / `.password-prompt-dialog__submit-btn` — hai nút footer.
 *
 * 🔴 Responsive ≤767px (SP-005, Task 9): đây là hộp thoại NGẮN (BRD §10) — GIỮ modal căn giữa,
 * KHÔNG thành overlay toàn màn. Chỉ đổi `width` từ `480` sang `LINKED_ACCOUNT_SHORT_MODAL_WIDTH_PX`
 * (343, hằng dùng chung cho mọi hộp thoại ngắn của cụm này) khi `useIsMobile()`.
 */

export interface PasswordPromptDialogProps {
  open: boolean
  title: string
  description: string
  submitting: boolean
  /** Câu lỗi hiện DƯỚI ô nhập. `null` = không có lỗi. */
  errorMessage: string | null
  onCancel: () => void
  onSubmit: (password: string) => void
}

const FIELD_LABEL = 'Mật khẩu xác thực'
const FIELD_PLACEHOLDER = 'Nhập mật khẩu'
const CANCEL_LABEL = 'Huỷ'
const CONFIRM_LABEL = 'Xác nhận'

const PasswordPromptDialog = ({
  open, title, description, submitting, errorMessage, onCancel, onSubmit,
}: PasswordPromptDialogProps): JSX.Element => {
  const [password, setPassword] = useState('')
  const isMobile = useIsMobile()

  // Mỗi lần MỞ (kể cả cùng instance, không unmount) ⇒ ô luôn rỗng, không nhớ lần trước.
  useEffect(() => {
    if (open) setPassword('')
  }, [open])

  const handleSubmit = (): void => {
    if (submitting) return
    if (password === '') return
    onSubmit(password)
    // Xoá ngay sau mỗi lần bấm Xác nhận — kể cả khi `onSubmit` sau đó báo lỗi (Global Constraint 8).
    setPassword('')
  }

  return (
    <Modal
      className={`${LINKED_ACCOUNT_DIALOG_CLASS} ${LINKED_ACCOUNT_DIALOG_PROMPT_CLASS} password-prompt-dialog`}
      open={open}
      width={isMobile ? LINKED_ACCOUNT_SHORT_MODAL_WIDTH_PX : LINKED_ACCOUNT_PROMPT_MODAL_WIDTH_PX}
      destroyOnClose
      maskClosable={false}
      title={title}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" size="large" className="password-prompt-dialog__cancel-btn" onClick={onCancel}>
          {CANCEL_LABEL}
        </Button>,
        <Button
          key="submit"
          type="primary"
          size="large"
          className="password-prompt-dialog__submit-btn"
          loading={submitting}
          onClick={handleSubmit}
        >
          {CONFIRM_LABEL}
        </Button>,
      ]}
    >
      <p className="password-prompt-dialog__description">{description}</p>
      <div className="password-prompt-dialog__field">
        <label className="password-prompt-dialog__label" htmlFor="password-prompt-dialog-input">
          {FIELD_LABEL}
          {' '}
          <span className="password-prompt-dialog__required">*</span>
        </label>
        <Input.Password
          id="password-prompt-dialog-input"
          size="large"
          // 20/09/2026 — BẬT icon con mắt theo yêu cầu người dùng. Figma `26:9882` không vẽ icon
          // này, nhưng ô chỉ chứa chuỗi người đang đăng nhập TỰ GÕ nên hiện lại không phơi bí mật
          // nào của hệ thống. `visibilityToggle` để mặc định (`true`) ⇒ antd bọc thêm
          // `.ant-input-affix-wrapper`: className rơi lên WRAPPER chứ không lên `<input>` nữa, nên
          // SCSS và test phải nhận cả hai dạng selector.
          iconRender={renderPasswordVisibilityIcon}
          className="password-prompt-dialog__input"
          // Figma trạng thái sai mật khẩu: viền ô chuyển đỏ cùng lúc với câu lỗi bên dưới.
          status={errorMessage !== null ? 'error' : undefined}
          placeholder={FIELD_PLACEHOLDER}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          onPressEnter={handleSubmit}
        />
        {errorMessage !== null && (
          <p className="password-prompt-dialog__error">
            <FieldErrorText text={errorMessage} />
          </p>
        )}
      </div>
    </Modal>
  )
}

export default PasswordPromptDialog
